import { createContext, useContext, useMemo, useState } from 'react';

import { BUILDER_DEVICE_MODE } from 'hb-react/backoffice/types/builder/builderDocument';

interface DeviceModeProviderProps {
  children: React.ReactNode;
}

interface DeviceModeContextValue {
  currentDeviceMode: BUILDER_DEVICE_MODE;
  isDesktopMode: boolean;
  isMobileMode: boolean;
  setMobileMode: () => void;
  setDesktopMode: () => void;
}

const DeviceModeContext = createContext<DeviceModeContextValue | undefined>(
  undefined,
);

export const DeviceModeProvider = ({ children }: DeviceModeProviderProps) => {
  const [currentDeviceMode, setCurrentDeviceMode] = useState(
    BUILDER_DEVICE_MODE.DESKTOP,
  );

  const setMobileMode = () => setCurrentDeviceMode(BUILDER_DEVICE_MODE.MOBILE);
  const setDesktopMode = () =>
    setCurrentDeviceMode(BUILDER_DEVICE_MODE.DESKTOP);

  const value = useMemo(
    () => ({
      setMobileMode,
      setDesktopMode,
      currentDeviceMode,
      isDesktopMode: currentDeviceMode === BUILDER_DEVICE_MODE.DESKTOP,
      isMobileMode: currentDeviceMode === BUILDER_DEVICE_MODE.MOBILE,
    }),
    [currentDeviceMode],
  );

  return (
    <DeviceModeContext.Provider value={value}>
      {children}
    </DeviceModeContext.Provider>
  );
};

export const useDeviceModeContext = () => {
  const context = useContext(DeviceModeContext);

  if (!context) {
    throw new Error(
      'useDeviceModeContext must be used within DeviceModeProvider',
    );
  }

  return context;
};

export default DeviceModeContext;
